import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { CanonicalTag } from '@/components/CanonicalTag';
import { StructuredData } from '@/components/StructuredData';
import finishLineImage from '@/assets/triathlon-finish-line.jpg';

const About = () => {
  const navigate = useNavigate();

  const userTypes = [
    { title: 'First-Timers', description: 'Comprehensive guidance for your first race with a proven checklist' },
    { title: 'Veterans', description: 'Never forget the essentials again with customizable templates' },
    { title: 'All Distances', description: 'Sprint to iron-distance fully supported' }
  ];

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Sink Bike Run",
    "url": "https://sinkbikerun.com",
    "logo": "https://sinkbikerun.com/favicon.svg",
    "description": "Free triathlon race day checklist builder and organizer for athletes of all levels",
    "sameAs": []
  };

  const aboutPageSchema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About Sink Bike Run - Built by Athletes for Athletes",
    "description": "Learn about Sink Bike Run, the free triathlon race day checklist tool built by triathletes for triathletes. Discover how we help athletes never forget essential gear.",
    "url": "https://sinkbikerun.com/about",
    "mainEntity": {
      "@type": "Organization",
      "name": "Sink Bike Run"
    }
  };

  return (
    <>
      <Helmet>
        <title>About Us | Custom Checklist Tool for Triathletes</title>
        <meta 
          name="description" 
          content="Sink Bike Run is a free triathlon race day checklist tool. Build custom checklists, personalize templates, print organized lists, and ensure you're race-ready. Built by triathletes for triathletes." 
        />
        <meta name="keywords" content="triathlon checklist, race day preparation, triathlon packing list, race day organization, triathlon gear checklist, about sink bike run" />
        
        {/* Preload hero image */}
        <link rel="preload" as="image" href={finishLineImage} fetchPriority="high" />
        
        {/* Open Graph tags */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="About Us | Custom Checklist Tool for Triathletes" />
        <meta property="og:description" content="Free triathlon race day checklist tool. Build custom checklists, personalize templates, and ensure you're race-ready." />
        <meta property="og:url" content="https://sinkbikerun.com/about" />
        <meta property="og:image" content="https://sinkbikerun.com/og-image.jpg" />
        
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Us | Custom Checklist Tool for Triathletes" />
        <meta name="twitter:description" content="Free triathlon race day checklist tool. Build custom checklists, personalize templates, and ensure you're race-ready." />
        <meta name="twitter:image" content="https://sinkbikerun.com/og-image.jpg" />
      </Helmet>
      <CanonicalTag url="/about" />
      <StructuredData data={[organizationSchema, aboutPageSchema]} />

      <div className="min-h-screen flex flex-col">
        <a href="#main-content" className="skip-link">Skip to main content</a>
        <Header showSearch={false} transparentOnMobile={true} />

        {/* Hero Section */}
        <section className="relative min-h-[50vh] sm:min-h-[60vh] flex items-center justify-center overflow-hidden">
          <img 
            src={finishLineImage}
            alt="Triathlon athletes at finish line"
            className="absolute inset-0 w-full h-full"
            data-hero-image="true"
            style={{ objectFit: 'cover' }}
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-background" />
          <div className="relative z-10 max-w-4xl mx-auto px-6 py-8 sm:py-0 text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-semibold text-white mb-4 sm:mb-6">
              Built by Athletes, For Athletes
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl font-body text-white/90 max-w-2xl mx-auto leading-relaxed">
              The complete race day preparation system
            </p>
          </div>
        </section>

        {/* Main Content */}
        <main id="main-content" className="flex-grow bg-background">
          {/* Why Sink Bike Run Exists */}
          <section className="py-6 sm:py-8 md:py-10">
            <div className="max-w-4xl mx-auto px-6">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-semibold text-center mb-6 sm:mb-8">Why Sink Bike Run Exists</h2>
              <div className="space-y-6 text-base font-body text-muted-foreground leading-[1.7]">
                <p>
                  When race week hits and it's time to start packing, the questions start flooding in. Spare tubes? Check. Nutrition? Maybe. Race belt? Probably. The problem isn't just remembering individual items—it's the nagging fear that you're missing something important, and you won't realize it until it's too late.
                </p>
                <p>
                  Sink Bike Run is a free checklist tool built specifically for triathletes. It helps you organize everything you need for race day—from transition gear to pre-race nutrition—so you can stop relying on scattered notes or generic packing lists that miss the things that matter to <em>your</em> race.
                </p>
                <p>
                  Whether you're building a checklist from scratch, using an event-specific template, or starting with a proven sample, you get a system that adapts to your needs. Organize by race phase, print clean copies, or share with your training partners. It's simple, flexible, and purpose-built for triathletes who want their race preparation as dialed in as their training.
                </p>
              </div>
            </div>
          </section>

          {/* Mid-page CTA */}
          <section className="py-6 sm:py-8 md:py-10">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-semibold mb-4">Pack Smart. Race Confident.</h2>
              <p className="text-base font-body text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto leading-[1.65]">
                From travel bags to race gear, nutrition to transitions—build a checklist that keeps your entire triathlon prep organized and stress-free.
              </p>
              <div className="flex justify-center">
                <Button 
                  size="lg" 
                  onClick={() => navigate('/#create-checklist')}
                  className="w-full md:w-auto bg-primary hover:bg-primary-dark text-primary-foreground h-11 sm:h-12 md:h-14 px-5 sm:px-6 md:px-8 text-base md:text-lg rounded-sm transition-all duration-200 shadow-modern hover:shadow-hero"
                >
                  Create Your Checklist
                </Button>
              </div>
            </div>
          </section>

          {/* What You Can Do */}
          <section className="py-6 sm:py-8 md:py-10 bg-muted/20">
            <div className="max-w-4xl mx-auto px-6">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-semibold text-center mb-8 sm:mb-12">What You Can Do</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Build & Customize */}
                <div className="text-center">
                  <h3 className="text-lg sm:text-xl font-display font-semibold mb-4">Build & Customize</h3>
                  <p className="text-base font-body text-muted-foreground leading-[1.7]">
                    Start from a proven race day template, use an event-specific checklist template, or build your own from scratch. Add your specific gear, nutrition, pre-race rituals, and notes. Every checklist adapts to your preferences and race distance.
                  </p>
                </div>

                {/* Share & Collaborate */}
                <div className="text-center">
                  <h3 className="text-lg sm:text-xl font-display font-semibold mb-4">Share & Collaborate</h3>
                  <p className="text-base font-body text-muted-foreground leading-[1.7]">
                    Share checklists with your coach, training partners, or support crew via email or link. Get feedback, coordinate logistics, or help first-timers with a proven list. Collaboration makes race prep easier.
                  </p>
                </div>

                {/* Organize Your Way */}
                <div className="text-center">
                  <h3 className="text-lg sm:text-xl font-display font-semibold mb-4">Organize Your Way</h3>
                  <p className="text-base font-body text-muted-foreground leading-[1.7]">
                    Structure checklists by race phase (Swim, T1, Bike, T2, Run), by location (home, car, transition), or create custom sections that match your workflow. Your organization, your rules.
                  </p>
                </div>

                {/* Access Anywhere */}
                <div className="text-center">
                  <h3 className="text-lg sm:text-xl font-display font-semibold mb-4">Access Anywhere</h3>
                  <p className="text-base font-body text-muted-foreground leading-[1.7]">
                    Real-time cloud sync keeps your checklists updated across all devices. Mobile-optimized for on-the-go access.
                  </p>
                </div>

                {/* Smart Search */}
                <div className="text-center">
                  <h3 className="text-lg sm:text-xl font-display font-semibold mb-4">Smart Search</h3>
                  <p className="text-base font-body text-muted-foreground leading-[1.7]">
                    Find items across all your checklists instantly. Never wonder which list has your spare goggles.
                  </p>
                </div>

                {/* Race Day Ready */}
                <div className="text-center">
                  <h3 className="text-lg sm:text-xl font-display font-semibold mb-4">Race Day Ready</h3>
                  <p className="text-base font-body text-muted-foreground leading-[1.7]">
                    Countdown timers track days until race day. Print clean, organized lists for transition setup.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Who It's For */}
          <section className="py-6 sm:py-8 md:py-10">
            <div className="max-w-4xl mx-auto px-6">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-semibold text-center mb-8 sm:mb-12">Who It's For</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                {userTypes.map((type) => (
                  <div key={type.title} className="border border-border rounded-lg p-6 text-center">
                    <h3 className="text-base sm:text-lg md:text-xl font-display font-semibold mb-3">{type.title}</h3>
                    <p className="text-base font-body text-muted-foreground leading-[1.65]">{type.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* About the Creator */}
          <section className="py-6 sm:py-8 md:py-10 bg-muted/20">
            <div className="max-w-4xl mx-auto px-6">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-semibold text-center mb-6 sm:mb-8">About the Creator</h2>
              <div className="border border-border rounded-lg p-6 md:p-8">
                <p className="text-base font-body text-muted-foreground leading-[1.7] mb-4">
                  Sink Bike Run was born from a simple fear: forgetting something crucial on race day. Standing in my 
                  garage preparing for a race, I kept thinking: what if I forget my spare tube? What if I'm missing 
                  nutrition? What if something small ruins months of training? The scattered notes and generic packing 
                  lists weren't enough to quiet that fear.
                </p>
                <p className="text-base font-body text-muted-foreground leading-[1.7] mb-4">
                  I needed a tool I could fine-tune from race to race—learning what worked, adjusting for different 
                  distances, building a system that got better over time. Something flexible yet structured enough to 
                  ensure nothing was forgotten. So I built it. Now, whether you're tackling your first sprint or your 
                  tenth iron-distance race, you can build, customize, and refine your perfect race day checklist.
                </p>
                <p className="text-base font-body text-muted-foreground leading-[1.7]">
                  Affiliate Disclosure: Sink Bike Run participates in affiliate programs, including the Amazon Services 
                  LLC Associates Program. As an Amazon Associate, I earn from qualifying purchases. When you click on 
                  affiliate links and make a purchase, I may earn a commission at no additional cost to you. This helps 
                  support the continued development and maintenance of Sink Bike Run.
                </p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-6 sm:py-8 md:py-10">
            <div className="max-w-4xl mx-auto px-6 text-center">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-display font-semibold mb-4 sm:mb-6">Ready to Get Organized?</h2>
              <p className="text-base font-body text-muted-foreground mb-6 sm:mb-8 max-w-2xl mx-auto leading-[1.65]">
                Join athletes who trust Sink Bike Run to keep their race day preparation on track.
              </p>
              <div className="flex justify-center">
                <Button 
                  size="lg" 
                  onClick={() => navigate('/#create-checklist')}
                  className="w-full md:w-auto bg-primary hover:bg-primary-dark text-primary-foreground h-11 sm:h-12 md:h-14 px-5 sm:px-6 md:px-8 text-base md:text-lg rounded-sm transition-all duration-200 shadow-modern hover:shadow-hero"
                >
                  Create Your Checklist
                </Button>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default About;